// src/pages/AssignManager.js

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import axios from "axios";

/*
 * Existing OPPIES Admin API.
 *
 * This already exists in the Admin Portal and
 * gives us the complete list of Op Shops.
 */
const GET_ALL_SHOPS_API =
    "https://oppiesfunc.azurewebsites.net/api/getAllShops?code=Dx_kliFsd4-5j40_pt5i6RsR36dv01K92JHMPb6eQRHBAzFuQKNV3A==";

/*
 * ------------------------------------------------------------------
 * MANAGER APIs
 * ------------------------------------------------------------------
 *
 * Add the Function keys from Azure after the two Functions
 * have been deployed.
 *
 * Example:
 *
 * https://oppiesfunc.azurewebsites.net/api/updateManagerAccess?code=xxxxx
 *
 * https://oppiesfunc.azurewebsites.net/api/getManagedShops?code=xxxxx
 */

const UPDATE_MANAGER_ACCESS_API =
    "https://oppiesfunc.azurewebsites.net/api/updateManagerAccess?code=-4qSaJmBSD0cyQhyFL8m15cTDmaDvskPy9qQADCMKHdHAzFu51c-Og==";

const GET_MANAGED_SHOPS_API =
    "https://oppiesfunc.azurewebsites.net/api/getManagedShops?code=huECMxImipiK72eFqefelG3Vcf77HzRyN_x22uPFI5UHAzFuRPLsYw==";


const OPPIES_BEIGE = "#bfa97c";

const TEXT_DARK = "#3c3329";
const TEXT_MUTED = "#857969";

const GREEN = "#1f5130";
const LIGHT_GREEN = "#edf5ef";

const BORDER = "#e4ddd1";
const PAGE_BG = "#fffaf1";


export default function AssignManager() {

    /*
     * --------------------------------------------------------------
     * DATA
     * --------------------------------------------------------------
     */

    const [
        shops,
        setShops,
    ] = useState([]);

    const [
        loadingShops,
        setLoadingShops,
    ] = useState(true);


    /*
     * --------------------------------------------------------------
     * MANAGER ACCOUNT SEARCH
     * --------------------------------------------------------------
     */

    const [
        emailInput,
        setEmailInput,
    ] = useState("");

    const [
        managerAccount,
        setManagerAccount,
    ] = useState(null);

    const [
        findingManager,
        setFindingManager,
    ] = useState(false);


    /*
     * --------------------------------------------------------------
     * MANAGER SETTINGS
     * --------------------------------------------------------------
     */

    const [
        managerEnabled,
        setManagerEnabled,
    ] = useState(false);

    const [
        organisationName,
        setOrganisationName,
    ] = useState("");

    const [
        organisationId,
        setOrganisationId,
    ] = useState("");

    const [
        selectedShopIds,
        setSelectedShopIds,
    ] = useState([]);


    /*
     * --------------------------------------------------------------
     * SHOP SEARCH
     * --------------------------------------------------------------
     */

    const [
        shopSearch,
        setShopSearch,
    ] = useState("");


    /*
     * --------------------------------------------------------------
     * PAGE STATE
     * --------------------------------------------------------------
     */

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        status,
        setStatus,
    ] = useState({
        type: "",
        message: "",
    });


    /*
     * --------------------------------------------------------------
     * LOAD ALL SHOPS
     * --------------------------------------------------------------
     */

    useEffect(() => {

        let mounted = true;

        const fetchShops = async () => {

            setLoadingShops(true);

            try {

                const response =
                    await axios.get(
                        GET_ALL_SHOPS_API,
                        {
                            timeout: 30000,
                        }
                    );

                if (!mounted) {
                    return;
                }

                const list =
                    Array.isArray(
                        response?.data
                    )
                        ? response.data
                        : [];


                /*
                 * Sort using the friendly display name.
                 *
                 * IMPORTANT:
                 *
                 * We never change or regenerate shopId.
                 * Historical OPPIES shop IDs are retained
                 * exactly as stored in Cosmos.
                 */

                const sorted =
                    [...list].sort(
                        (a, b) =>
                            String(
                                a?.name ||
                                a?.shopId ||
                                ""
                            ).localeCompare(
                                String(
                                    b?.name ||
                                    b?.shopId ||
                                    ""
                                )
                            )
                    );

                setShops(sorted);

            } catch (error) {

                console.error(
                    "❌ Failed to load OPPIES shops:",
                    error?.response?.data ||
                    error?.message ||
                    error
                );

                if (mounted) {

                    setStatus({
                        type: "error",
                        message:
                            "Could not load the OPPIES shop list.",
                    });

                }

            } finally {

                if (mounted) {
                    setLoadingShops(false);
                }

            }

        };


        fetchShops();


        return () => {
            mounted = false;
        };

    }, []);


    /*
     * --------------------------------------------------------------
     * FILTER SHOP LIST
     * --------------------------------------------------------------
     */

    const filteredShops =
        useMemo(() => {

            const term =
                shopSearch
                    .trim()
                    .toLowerCase();


            if (!term) {

                /*
                 * Avoid dropping hundreds of shops
                 * into the page initially.
                 */

                return shops.slice(0, 40);

            }


            return shops
                .filter((shop) => {

                    const haystack = [
                        shop?.name,
                        shop?.shopId,
                        shop?.email,
                        shop?.address,
                        shop?.suburb,
                        shop?.city,
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();


                    return haystack.includes(
                        term
                    );

                })
                .slice(0, 80);

        }, [
            shops,
            shopSearch,
        ]);


    /*
     * --------------------------------------------------------------
     * SELECTED SHOP OBJECTS
     * --------------------------------------------------------------
     */

    const selectedShops =
        useMemo(() => {

            const selectedSet =
                new Set(
                    selectedShopIds
                );


            return shops.filter(
                (shop) =>
                    selectedSet.has(
                        shop.shopId
                    )
            );

        }, [
            shops,
            selectedShopIds,
        ]);


    /*
     * --------------------------------------------------------------
     * ORGANISATION ID HELPER
     * --------------------------------------------------------------
     *
     * This is only a convenience helper for the Admin.
     *
     * Unlike shopId, organisationId is a NEW identifier,
     * so generating a slug here is safe.
     */

    const generateOrganisationId =
        (name) => {

            return String(
                name || ""
            )
                .trim()
                .toLowerCase()
                .normalize("NFD")
                .replace(
                    /[\u0300-\u036f]/g,
                    ""
                )
                .replace(
                    /[^a-z0-9]+/g,
                    "-"
                )
                .replace(
                    /^-+|-+$/g,
                    "");

        };


    const handleOrganisationNameChange =
        (event) => {

            const value =
                event.target.value;

            setOrganisationName(
                value
            );


            /*
             * Auto-create organisation ID only
             * while Admin has not manually edited it.
             */

            if (
                !organisationId ||
                organisationId ===
                    generateOrganisationId(
                        organisationName
                    )
            ) {

                setOrganisationId(
                    generateOrganisationId(
                        value
                    )
                );

            }

        };


    /*
     * --------------------------------------------------------------
     * RESET FORM
     * --------------------------------------------------------------
     */

    const resetManagerForm = () => {

        setManagerAccount(null);

        setManagerEnabled(false);

        setOrganisationName("");

        setOrganisationId("");

        setSelectedShopIds([]);

        setShopSearch("");

    };


    /*
     * --------------------------------------------------------------
     * FIND MANAGER ACCOUNT
     * --------------------------------------------------------------
     */

    const handleFindAccount =
        async () => {

            const email =
                emailInput
                    .trim()
                    .toLowerCase();


            if (!email) {

                setStatus({
                    type: "error",
                    message:
                        "Enter the Op Shop account email address.",
                });

                return;

            }


            setFindingManager(true);

            setStatus({
                type: "",
                message: "",
            });

            resetManagerForm();


            try {

                /*
                 * Find the existing OPPIES shop/account
                 * associated with this email.
                 *
                 * The manager must already have gone
                 * through the normal OPPIES account /
                 * ownership process.
                 */

                const matchingAccounts =
                    shops.filter(
                        (shop) =>
                            String(
                                shop?.email || ""
                            )
                                .trim()
                                .toLowerCase() ===
                            email
                    );


                if (
                    matchingAccounts.length ===
                    0
                ) {

                    setStatus({
                        type: "error",
                        message:
                            "No OPPIES Op Shop account was found for that email address. The account must exist before Manager Access can be assigned.",
                    });

                    return;

                }


                if (
                    matchingAccounts.length >
                    1
                ) {

                    console.warn(
                        "⚠️ Multiple shop documents use this email:",
                        matchingAccounts
                    );

                }


                const account =
                    matchingAccounts[0];


                setManagerAccount({
                    email,
                    name:
                        account?.name ||
                        "",
                    shopId:
                        account?.shopId ||
                        "",
                });


                /*
                 * --------------------------------------------------
                 * CHECK WHETHER THEY ARE ALREADY A MANAGER
                 * --------------------------------------------------
                 */

                try {

                    const separator =
                        GET_MANAGED_SHOPS_API.includes(
                            "?"
                        )
                            ? "&"
                            : "?";


                    const managerResponse =
                        await axios.get(
                            `${GET_MANAGED_SHOPS_API}${separator}email=${encodeURIComponent(
                                email
                            )}`,
                            {
                                timeout: 20000,
                            }
                        );


                    const data =
                        managerResponse?.data ||
                        {};


                    setManagerEnabled(
                        data?.manager === true ||
                        data?.manager?.enabled ===
                            true
                    );


                    setOrganisationName(
                        data?.organisation
                            ?.name ||
                        data?.organisationName ||
                        ""
                    );


                    setOrganisationId(
                        data?.organisation
                            ?.id ||
                        data?.organisationId ||
                        ""
                    );


                    const managedShopIds =
                        Array.isArray(
                            data?.shops
                        )
                            ? data.shops
                                .map(
                                    (shop) =>
                                        shop?.shopId
                                )
                                .filter(Boolean)
                            : [];


                    setSelectedShopIds(
                        managedShopIds
                    );


                    setStatus({
                        type: "success",
                        message:
                            managedShopIds.length >
                            0
                                ? `Existing manager loaded. ${managedShopIds.length} authorised shop${managedShopIds.length === 1 ? "" : "s"} found.`
                                : "Existing manager account loaded.",
                    });


                } catch (managerError) {

                    /*
                     * getManagedShops intentionally returns
                     * 403 for a normal account.
                     *
                     * That is NOT an error here.
                     * It simply means Admin is creating
                     * manager access for the first time.
                     */

                    const responseStatus =
                        managerError
                            ?.response
                            ?.status;


                    if (
                        responseStatus ===
                        403
                    ) {

                        setManagerEnabled(
                            false
                        );

                        setStatus({
                            type: "info",
                            message:
                                "Account found. This account does not currently have Manager Access.",
                        });

                    } else {

                        console.warn(
                            "⚠️ Could not read current manager configuration:",
                            managerError
                                ?.response
                                ?.data ||
                            managerError
                                ?.message ||
                            managerError
                        );


                        setStatus({
                            type: "info",
                            message:
                                "Account found. Current Manager Access could not be checked, but you can still configure it below.",
                        });

                    }

                }


            } finally {

                setFindingManager(
                    false
                );

            }

        };


    /*
     * --------------------------------------------------------------
     * SHOP SELECTION
     * --------------------------------------------------------------
     */

    const toggleShop =
        (shopId) => {

            if (!shopId) {
                return;
            }


            setSelectedShopIds(
                (previous) => {

                    if (
                        previous.includes(
                            shopId
                        )
                    ) {

                        return previous.filter(
                            (id) =>
                                id !== shopId
                        );

                    }


                    return [
                        ...previous,
                        shopId,
                    ];

                }
            );

        };


    const removeSelectedShop =
        (shopId) => {

            setSelectedShopIds(
                (previous) =>
                    previous.filter(
                        (id) =>
                            id !== shopId
                    )
            );

        };


    /*
     * --------------------------------------------------------------
     * SAVE MANAGER
     * --------------------------------------------------------------
     */

    const handleSaveManager =
        async () => {

            if (!managerAccount?.email) {

                setStatus({
                    type: "error",
                    message:
                        "Find an OPPIES account first.",
                });

                return;

            }


            const cleanOrganisationName =
                organisationName.trim();

            const cleanOrganisationId =
                organisationId.trim();


            if (
                !cleanOrganisationName
            ) {

                setStatus({
                    type: "error",
                    message:
                        "Organisation name is required.",
                });

                return;

            }


            if (
                !cleanOrganisationId
            ) {

                setStatus({
                    type: "error",
                    message:
                        "Organisation ID is required.",
                });

                return;

            }


            if (
                selectedShopIds.length ===
                0
            ) {

                setStatus({
                    type: "error",
                    message:
                        "Select at least one Op Shop for this manager.",
                });

                return;

            }


            const confirmed =
                window.confirm(
                    `Enable Manager Access for ${managerAccount.email}?\n\nOrganisation: ${cleanOrganisationName}\nAuthorised shops: ${selectedShopIds.length}`
                );


            if (!confirmed) {
                return;
            }


            setSaving(true);

            setStatus({
                type: "info",
                message:
                    "Saving Manager Access...",
            });


            try {

                const payload = {

                    email:
                        managerAccount.email,

                    enabled: true,

                    organisationId:
                        cleanOrganisationId,

                    organisationName:
                        cleanOrganisationName,

                    /*
                     * IMPORTANT:
                     *
                     * These are the EXACT shopIds
                     * from Cosmos.
                     *
                     * No slug generation.
                     * No organisation name matching.
                     * No inferred access.
                     */
                    shopIds:
                        selectedShopIds,

                    approvedBy:
                        "OPPIES Admin",
                };


                console.log(
                    "📤 updateManagerAccess:",
                    payload
                );


                const response =
                    await axios.post(
                        UPDATE_MANAGER_ACCESS_API,
                        payload,
                        {
                            timeout: 30000,
                        }
                    );


                setManagerEnabled(
                    true
                );


                setStatus({
                    type: "success",
                    message:
                        `Manager Access saved. ${managerAccount.email} can now manage ${selectedShopIds.length} Op Shop${selectedShopIds.length === 1 ? "" : "s"}.`,
                });


                console.log(
                    "✅ Manager Access saved:",
                    response?.data
                );


            } catch (error) {

                console.error(
                    "❌ updateManagerAccess failed:",
                    error?.response?.data ||
                    error?.message ||
                    error
                );


                setStatus({
                    type: "error",
                    message:
                        error?.response?.data
                            ?.error ||
                        "Could not save Manager Access.",
                });


            } finally {

                setSaving(false);

            }

        };


    /*
     * --------------------------------------------------------------
     * DISABLE MANAGER ACCESS
     * --------------------------------------------------------------
     */

    const handleDisableManager =
        async () => {

            if (!managerAccount?.email) {
                return;
            }


            const confirmed =
                window.confirm(
                    `Disable Manager Access for ${managerAccount.email}?\n\nThey will immediately lose access to all co-managed shops. Their normal Op Shop account will remain active.`
                );


            if (!confirmed) {
                return;
            }


            setSaving(true);

            setStatus({
                type: "info",
                message:
                    "Disabling Manager Access...",
            });


            try {

                await axios.post(
                    UPDATE_MANAGER_ACCESS_API,
                    {
                        email:
                            managerAccount.email,

                        enabled: false,

                        approvedBy:
                            "OPPIES Admin",
                    },
                    {
                        timeout: 30000,
                    }
                );


                setManagerEnabled(
                    false
                );

                setOrganisationName("");

                setOrganisationId("");

                setSelectedShopIds([]);


                setStatus({
                    type: "success",
                    message:
                        `Manager Access disabled for ${managerAccount.email}. Their normal Op Shop access remains unchanged.`,
                });


            } catch (error) {

                console.error(
                    "❌ Disable Manager Access failed:",
                    error?.response?.data ||
                    error?.message ||
                    error
                );


                setStatus({
                    type: "error",
                    message:
                        error?.response?.data
                            ?.error ||
                        "Could not disable Manager Access.",
                });


            } finally {

                setSaving(false);

            }

        };


    /*
     * --------------------------------------------------------------
     * RENDER
     * --------------------------------------------------------------
     */

    return (

        <div style={styles.page}>

            <div style={styles.header}>

                <div>

                    <div style={styles.eyebrow}>
                        APPROVALS
                    </div>

                    <h1 style={styles.title}>
                        Assign Manager
                    </h1>

                    <p style={styles.subtitle}>
                        Give an approved Head Office or regional account permission to co-manage selected OPPIES Op Shops.
                    </p>

                </div>

            </div>


            {status.message && (

                <div
                    style={
                        styles.status(
                            status.type
                        )
                    }
                >
                    {status.message}
                </div>

            )}


            {/* ==================================================
                STEP 1 — FIND ACCOUNT
            ================================================== */}

            <section style={styles.card}>

                <div style={styles.sectionHeadingRow}>

                    <div style={styles.stepNumber}>
                        1
                    </div>

                    <div>
                        <h2 style={styles.sectionTitle}>
                            Manager Account
                        </h2>

                        <p style={styles.sectionDescription}>
                            The person must already have an approved OPPIES Op Shop account.
                        </p>
                    </div>

                </div>


                <div style={styles.fieldRow}>

                    <div style={styles.flexField}>

                        <label style={styles.label}>
                            Account email
                        </label>

                        <input
                            type="email"
                            value={emailInput}
                            onChange={(event) =>
                                setEmailInput(
                                    event.target.value
                                )
                            }
                            onKeyDown={(event) => {

                                if (
                                    event.key ===
                                    "Enter"
                                ) {

                                    handleFindAccount();

                                }

                            }}
                            placeholder="head.office@organisation.org.nz"
                            style={styles.input}
                            disabled={
                                findingManager ||
                                saving
                            }
                        />

                    </div>


                    <button
                        type="button"
                        onClick={
                            handleFindAccount
                        }
                        disabled={
                            findingManager ||
                            loadingShops
                        }
                        style={{
                            ...styles.primaryButton,
                            ...(
                                findingManager ||
                                loadingShops
                                    ? styles.disabledButton
                                    : {}
                            ),
                        }}
                    >
                        {findingManager
                            ? "Finding..."
                            : "Find Account"}
                    </button>

                </div>


                {managerAccount && (

                    <div style={styles.accountFound}>

                        <div style={styles.accountIcon}>
                            ✓
                        </div>

                        <div style={styles.accountDetails}>

                            <strong>
                                {
                                    managerAccount.email
                                }
                            </strong>

                            {managerAccount.name && (
                                <span>
                                    {
                                        managerAccount.name
                                    }
                                </span>
                            )}

                            {managerAccount.shopId && (
                                <span style={styles.smallMuted}>
                                    Existing shop:{" "}
                                    {
                                        managerAccount.shopId
                                    }
                                </span>
                            )}

                        </div>


                        <div
                            style={
                                managerEnabled
                                    ? styles.managerBadge
                                    : styles.normalBadge
                            }
                        >
                            {managerEnabled
                                ? "Manager Enabled"
                                : "Normal Account"}
                        </div>

                    </div>

                )}

            </section>


            {/* ==================================================
                STEP 2 — ORGANISATION
            ================================================== */}

            {managerAccount && (

                <section style={styles.card}>

                    <div style={styles.sectionHeadingRow}>

                        <div style={styles.stepNumber}>
                            2
                        </div>

                        <div>
                            <h2 style={styles.sectionTitle}>
                                Organisation
                            </h2>

                            <p style={styles.sectionDescription}>
                                Identify the charity or organisation this manager represents.
                            </p>
                        </div>

                    </div>


                    <div style={styles.twoColumn}>

                        <div>

                            <label style={styles.label}>
                                Organisation name
                            </label>

                            <input
                                type="text"
                                value={
                                    organisationName
                                }
                                onChange={
                                    handleOrganisationNameChange
                                }
                                placeholder="Hato Hone St John"
                                style={styles.input}
                                disabled={
                                    saving
                                }
                            />

                        </div>


                        <div>

                            <label style={styles.label}>
                                Organisation ID
                            </label>

                            <input
                                type="text"
                                value={
                                    organisationId
                                }
                                onChange={(event) =>
                                    setOrganisationId(
                                        event.target.value
                                    )
                                }
                                placeholder="hato-hone-st-john"
                                style={styles.input}
                                disabled={
                                    saving
                                }
                            />

                            <div style={styles.fieldHelp}>
                                Internal OPPIES identifier. It does not need to match historical shop IDs.
                            </div>

                        </div>

                    </div>

                </section>

            )}


            {/* ==================================================
                STEP 3 — AUTHORISED SHOPS
            ================================================== */}

            {managerAccount && (

                <section style={styles.card}>

                    <div style={styles.sectionHeadingRow}>

                        <div style={styles.stepNumber}>
                            3
                        </div>

                        <div style={{ flex: 1 }}>

                            <div style={styles.titleCountRow}>

                                <div>

                                    <h2 style={styles.sectionTitle}>
                                        Authorised Op Shops
                                    </h2>

                                    <p style={styles.sectionDescription}>
                                        Choose exactly which shops this account is allowed to co-manage.
                                    </p>

                                </div>


                                <div style={styles.shopCount}>
                                    {
                                        selectedShopIds.length
                                    }{" "}
                                    selected
                                </div>

                            </div>

                        </div>

                    </div>


                    <div style={styles.securityNotice}>

                        <strong>
                            Exact shop access only.
                        </strong>

                        <span>
                            Selecting one St John shop does not automatically grant access to every St John shop. Every branch must be explicitly selected.
                        </span>

                    </div>


                    {selectedShops.length >
                        0 && (

                        <div style={styles.selectedSection}>

                            <div style={styles.selectedHeader}>
                                Currently authorised
                            </div>


                            <div style={styles.selectedList}>

                                {selectedShops.map(
                                    (shop) => (

                                        <div
                                            key={
                                                shop.shopId
                                            }
                                            style={
                                                styles.selectedChip
                                            }
                                        >

                                            <div>

                                                <strong>
                                                    {
                                                        shop.name ||
                                                        shop.shopId
                                                    }
                                                </strong>

                                                <span>
                                                    {
                                                        shop.shopId
                                                    }
                                                </span>

                                            </div>


                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeSelectedShop(
                                                        shop.shopId
                                                    )
                                                }
                                                style={
                                                    styles.removeButton
                                                }
                                                title="Remove this shop"
                                            >
                                                ×
                                            </button>

                                        </div>

                                    )
                                )}

                            </div>

                        </div>

                    )}


                    <div style={styles.searchWrap}>

                        <label style={styles.label}>
                            Search Op Shops
                        </label>

                        <input
                            type="text"
                            value={
                                shopSearch
                            }
                            onChange={(event) =>
                                setShopSearch(
                                    event.target.value
                                )
                            }
                            placeholder="Search name, shopId, email, suburb or city..."
                            style={styles.input}
                        />

                    </div>


                    <div style={styles.shopList}>

                        {loadingShops ? (

                            <div style={styles.emptyState}>
                                Loading Op Shops...
                            </div>

                        ) : filteredShops.length ===
                          0 ? (

                            <div style={styles.emptyState}>
                                No Op Shops match your search.
                            </div>

                        ) : (

                            filteredShops.map(
                                (shop) => {

                                    const selected =
                                        selectedShopIds.includes(
                                            shop.shopId
                                        );


                                    return (

                                        <button
                                            key={
                                                shop.shopId
                                            }
                                            type="button"
                                            onClick={() =>
                                                toggleShop(
                                                    shop.shopId
                                                )
                                            }
                                            style={{
                                                ...styles.shopRow,
                                                ...(
                                                    selected
                                                        ? styles.shopRowSelected
                                                        : {}
                                                ),
                                            }}
                                        >

                                            <div
                                                style={{
                                                    ...styles.checkbox,
                                                    ...(
                                                        selected
                                                            ? styles.checkboxSelected
                                                            : {}
                                                    ),
                                                }}
                                            >
                                                {selected
                                                    ? "✓"
                                                    : ""}
                                            </div>


                                            <div style={styles.shopInfo}>

                                                <div style={styles.shopName}>
                                                    {
                                                        shop.name ||
                                                        shop.shopId
                                                    }
                                                </div>

                                                <div style={styles.shopMeta}>
                                                    <span>
                                                        ID:{" "}
                                                        {
                                                            shop.shopId
                                                        }
                                                    </span>

                                                    {shop.email && (
                                                        <span>
                                                            {
                                                                shop.email
                                                            }
                                                        </span>
                                                    )}

                                                    {(
                                                        shop.suburb ||
                                                        shop.city
                                                    ) && (
                                                        <span>
                                                            {[
                                                                shop.suburb,
                                                                shop.city,
                                                            ]
                                                                .filter(
                                                                    Boolean
                                                                )
                                                                .join(
                                                                    ", "
                                                                )}
                                                        </span>
                                                    )}
                                                </div>

                                            </div>


                                            {selected && (

                                                <div style={styles.authorisedText}>
                                                    Authorised
                                                </div>

                                            )}

                                        </button>

                                    );

                                }
                            )

                        )}

                    </div>

                </section>

            )}


            {/* ==================================================
                ACTIONS
            ================================================== */}

            {managerAccount && (

                <section style={styles.actionsCard}>

                    <div>

                        <strong style={styles.actionTitle}>
                            Manager Access
                        </strong>

                        <p style={styles.actionText}>

                            {managerEnabled
                                ? "Changes will update the shops currently available in this manager's OPPIES Portal dropdown."
                                : "Saving will enable Manager Access and make the selected shops available in their Portal dropdown."}

                        </p>

                    </div>


                    <div style={styles.actionButtons}>

                        {managerEnabled && (

                            <button
                                type="button"
                                onClick={
                                    handleDisableManager
                                }
                                disabled={
                                    saving
                                }
                                style={{
                                    ...styles.dangerButton,
                                    ...(
                                        saving
                                            ? styles.disabledButton
                                            : {}
                                    ),
                                }}
                            >
                                Disable Manager
                            </button>

                        )}


                        <button
                            type="button"
                            onClick={
                                handleSaveManager
                            }
                            disabled={
                                saving
                            }
                            style={{
                                ...styles.saveButton,
                                ...(
                                    saving
                                        ? styles.disabledButton
                                        : {}
                                ),
                            }}
                        >
                            {saving
                                ? "Saving..."
                                : managerEnabled
                                    ? "Save Manager Access"
                                    : "Make Manager"}
                        </button>

                    </div>

                </section>

            )}

        </div>

    );

}


/*
 * ==================================================================
 * STYLES
 * ==================================================================
 */

const styles = {

    page: {
        padding:
            "28px 30px 60px",
        maxWidth: 1180,
        margin: "0 auto",
        color: TEXT_DARK,
    },


    header: {
        display: "flex",
        justifyContent:
            "space-between",
        alignItems:
            "flex-start",
        marginBottom: 24,
    },


    eyebrow: {
        color: OPPIES_BEIGE,
        fontSize: 12,
        fontWeight: 900,
        letterSpacing: 1.2,
        marginBottom: 6,
    },


    title: {
        margin: 0,
        fontSize: 32,
        color: GREEN,
        fontWeight: 900,
        letterSpacing: -0.5,
    },


    subtitle: {
        margin:
            "8px 0 0",
        color: TEXT_MUTED,
        maxWidth: 700,
        lineHeight: 1.55,
        fontSize: 15,
    },


    status: (type) => ({

        marginBottom: 20,
        padding:
            "13px 16px",
        borderRadius: 12,
        border: "1px solid",

        background:
            type === "success"
                ? "#edf7ef"
                : type === "error"
                    ? "#fff0ef"
                    : "#fff9e9",

        borderColor:
            type === "success"
                ? "#bcd9c3"
                : type === "error"
                    ? "#ecc5c1"
                    : "#ead9a5",

        color:
            type === "success"
                ? "#205b32"
                : type === "error"
                    ? "#8a352f"
                    : "#715c20",

        fontSize: 14,
        fontWeight: 700,
    }),


    card: {
        background: "#ffffff",
        border:
            `1px solid ${BORDER}`,
        borderRadius: 18,
        padding: 24,
        marginBottom: 20,
        boxShadow:
            "0 8px 24px rgba(60,51,41,0.05)",
    },


    sectionHeadingRow: {
        display: "flex",
        alignItems:
            "flex-start",
        gap: 14,
        marginBottom: 22,
    },


    stepNumber: {
        width: 34,
        height: 34,
        borderRadius: 10,
        background: PAGE_BG,
        border:
            `1px solid ${OPPIES_BEIGE}`,
        color: GREEN,
        display: "flex",
        alignItems: "center",
        justifyContent:
            "center",
        fontSize: 14,
        fontWeight: 900,
        flexShrink: 0,
    },


    sectionTitle: {
        margin: 0,
        fontSize: 19,
        color: TEXT_DARK,
        fontWeight: 850,
    },


    sectionDescription: {
        margin:
            "5px 0 0",
        color: TEXT_MUTED,
        fontSize: 14,
        lineHeight: 1.45,
    },


    fieldRow: {
        display: "flex",
        alignItems:
            "flex-end",
        gap: 12,
    },


    flexField: {
        flex: 1,
    },


    twoColumn: {
        display: "grid",
        gridTemplateColumns:
            "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 18,
    },


    label: {
        display: "block",
        fontSize: 13,
        fontWeight: 800,
        color: TEXT_DARK,
        marginBottom: 7,
    },


    input: {
        width: "100%",
        boxSizing:
            "border-box",
        border:
            "1px solid #d8d0c4",
        borderRadius: 11,
        padding:
            "11px 13px",
        fontSize: 14,
        color: TEXT_DARK,
        background: "#ffffff",
        outline: "none",
    },


    fieldHelp: {
        marginTop: 6,
        color: TEXT_MUTED,
        fontSize: 12,
        lineHeight: 1.4,
    },


    primaryButton: {
        minHeight: 42,
        border: "none",
        borderRadius: 11,
        padding:
            "0 18px",
        background: GREEN,
        color: "#ffffff",
        fontSize: 14,
        fontWeight: 850,
        cursor: "pointer",
        whiteSpace:
            "nowrap",
    },


    accountFound: {
        marginTop: 18,
        padding: 14,
        borderRadius: 13,
        background:
            LIGHT_GREEN,
        border:
            "1px solid #cfe1d2",
        display: "flex",
        alignItems: "center",
        gap: 12,
    },


    accountIcon: {
        width: 34,
        height: 34,
        borderRadius: 999,
        background: GREEN,
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent:
            "center",
        fontWeight: 900,
    },


    accountDetails: {
        display: "flex",
        flexDirection:
            "column",
        gap: 2,
        flex: 1,
        fontSize: 14,
    },


    smallMuted: {
        fontSize: 12,
        color: TEXT_MUTED,
    },


    managerBadge: {
        padding:
            "6px 10px",
        borderRadius: 999,
        background: GREEN,
        color: "#ffffff",
        fontSize: 12,
        fontWeight: 850,
    },


    normalBadge: {
        padding:
            "6px 10px",
        borderRadius: 999,
        background: "#eee8de",
        color: TEXT_DARK,
        fontSize: 12,
        fontWeight: 850,
    },


    titleCountRow: {
        display: "flex",
        justifyContent:
            "space-between",
        alignItems:
            "flex-start",
        gap: 20,
    },


    shopCount: {
        padding:
            "6px 10px",
        borderRadius: 999,
        background: PAGE_BG,
        color: GREEN,
        border:
            `1px solid ${OPPIES_BEIGE}`,
        fontSize: 12,
        fontWeight: 850,
        whiteSpace:
            "nowrap",
    },


    securityNotice: {
        display: "flex",
        flexDirection:
            "column",
        gap: 4,
        padding:
            "12px 14px",
        borderRadius: 11,
        background: "#fff8e7",
        border:
            "1px solid #ead7a2",
        color: "#715b22",
        fontSize: 13,
        marginBottom: 20,
        lineHeight: 1.45,
    },


    selectedSection: {
        marginBottom: 20,
    },


    selectedHeader: {
        fontSize: 12,
        fontWeight: 850,
        color: TEXT_MUTED,
        textTransform:
            "uppercase",
        letterSpacing: 0.6,
        marginBottom: 9,
    },


    selectedList: {
        display: "flex",
        flexWrap: "wrap",
        gap: 9,
    },


    selectedChip: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding:
            "8px 9px 8px 11px",
        borderRadius: 11,
        background:
            LIGHT_GREEN,
        border:
            "1px solid #cfe1d2",
    },


    removeButton: {
        border: "none",
        background:
            "rgba(31,81,48,0.10)",
        width: 25,
        height: 25,
        borderRadius: 999,
        cursor: "pointer",
        color: GREEN,
        fontWeight: 900,
        fontSize: 18,
        lineHeight: 1,
    },


    searchWrap: {
        marginBottom: 12,
    },


    shopList: {
        border:
            `1px solid ${BORDER}`,
        borderRadius: 13,
        overflow: "hidden",
        maxHeight: 470,
        overflowY: "auto",
    },


    shopRow: {
        width: "100%",
        border: "none",
        borderBottom:
            `1px solid ${BORDER}`,
        background: "#ffffff",
        padding:
            "12px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        textAlign: "left",
        cursor: "pointer",
    },


    shopRowSelected: {
        background:
            "#f4f9f5",
    },


    checkbox: {
        width: 24,
        height: 24,
        borderRadius: 7,
        border:
            "1px solid #cfc5b6",
        display: "flex",
        alignItems: "center",
        justifyContent:
            "center",
        color: "#ffffff",
        fontSize: 14,
        fontWeight: 900,
        flexShrink: 0,
    },


    checkboxSelected: {
        background: GREEN,
        borderColor: GREEN,
    },


    shopInfo: {
        flex: 1,
        minWidth: 0,
    },


    shopName: {
        fontSize: 14,
        fontWeight: 800,
        color: TEXT_DARK,
    },


    shopMeta: {
        display: "flex",
        flexWrap: "wrap",
        gap:
            "4px 14px",
        marginTop: 4,
        color: TEXT_MUTED,
        fontSize: 11,
    },


    authorisedText: {
        color: GREEN,
        fontSize: 11,
        fontWeight: 850,
    },


    emptyState: {
        padding: 30,
        textAlign: "center",
        color: TEXT_MUTED,
        fontSize: 14,
    },


    actionsCard: {
        background:
            "linear-gradient(180deg, #fffdf9 0%, #f8f3eb 100%)",
        border:
            `1px solid ${BORDER}`,
        borderRadius: 18,
        padding:
            "20px 22px",
        display: "flex",
        justifyContent:
            "space-between",
        alignItems: "center",
        gap: 20,
        flexWrap: "wrap",
    },


    actionTitle: {
        color: TEXT_DARK,
        fontSize: 16,
    },


    actionText: {
        color: TEXT_MUTED,
        fontSize: 13,
        margin:
            "5px 0 0",
        maxWidth: 620,
        lineHeight: 1.4,
    },


    actionButtons: {
        display: "flex",
        gap: 10,
        alignItems: "center",
    },


    saveButton: {
        border: "none",
        borderRadius: 11,
        padding:
            "11px 18px",
        background: GREEN,
        color: "#ffffff",
        fontSize: 14,
        fontWeight: 850,
        cursor: "pointer",
    },


    dangerButton: {
        border:
            "1px solid #c9665e",
        borderRadius: 11,
        padding:
            "10px 16px",
        background: "#ffffff",
        color: "#9a3f38",
        fontSize: 14,
        fontWeight: 800,
        cursor: "pointer",
    },


    disabledButton: {
        opacity: 0.55,
        cursor: "not-allowed",
    },

};